import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { AppToolbar } from '../../components/AppToolbar';
import { BusyIcon } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useConfirm, useReasonGate, showDialog } from '../../components/confirm';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { runLoading } from '../../components/loading';
import { DEFAULT_DATE_RANGE, cutoffDate as sharedCutoff } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { fmtLocalDate } from '../../utils/datetime';
import { useNavActions } from '../../hooks/useNavActions';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SamplingAllocationRow {
  jobId:             string;
  client:            string | null;
  projectName:       string | null;
  projectCode:       string | null;
  jobIDCreatedDate:  string | null;
  eventDate:         string | null;
  updatedDate:       string | null;
  status:            string | null;
}

// One sample in the drill-down popup (a job's SampleLogIn rows) — GoLive
// SampleLogIn_ListView_SamplingAllocation columns.
interface SamplingAllocationSample {
  sampleMatrix:      string | null;
  siteName:          string | null;
  sampleId:          string | null;
  sampleName:        string | null;
  samplingEquipment: string | null;
  samplingDate:      string | null;
  assignTo:          string | null;
  dateAssigned:      string | null;
  assignedBy:        string | null;
  // SampleLogIn.Oid — the key the "Assign To" save updates by (not displayed).
  sampleLogInId:     string;
}

// 'all' sentinel + any StatusDefinition.Status value (data-driven chips).
type StatusFilter = string;
// DateRangeKey + cutoffDate now imported from listDefaults.ts.
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────────
// Render via the shared §14b.6 helper (local tz, MM/DD/YYYY); keep the '—'
// placeholder for empty/unparseable values used in grid cells + CSV export.
function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}

const cutoffDate = sharedCutoff;

// Most recent event date from a set of rows
function mostRecentEventDate(rows: SamplingAllocationRow[]): string {
  let best: Date | null = null;
  for (const r of rows) {
    if (!r.eventDate) continue;
    const d = new Date(r.eventDate);
    if (!isNaN(d.getTime()) && (!best || d > best)) best = d;
  }
  return best ? (fmtLocalDate(best.toISOString()) || '—') : '—';
}

// Column order used for CSV export (mirrors the grid columns).
const CSV_COLS: { key: keyof SamplingAllocationRow; header: string }[] = [
  { key: 'jobId',            header: 'Job ID' },
  { key: 'client',           header: 'Client' },
  { key: 'projectName',      header: 'Project Name' },
  { key: 'projectCode',      header: 'Project Code' },
  { key: 'jobIDCreatedDate', header: 'Job Created' },
  { key: 'eventDate',        header: 'Event Date' },
  { key: 'updatedDate',      header: 'Updated Date' },
  { key: 'status',           header: 'Status' },
];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Trigger a browser download of a blob (auth'd GET can't use a plain <a href>).
function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function SamplingAllocationPage() {
  const [rows,         setRows]         = useState<SamplingAllocationRow[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [loadMs,       setLoadMs]       = useState<number | null>(null);
  const [error,        setError]        = useState<string>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Module Access Control (Role-Based MAC) action gate — Users → Role → MAC on the
  // Sampling Allocation nav view (Samplecheckin_ListView_SamplingAllocation). Hides the
  // mutating actions when the signed-in user's role isn't granted them; falls open for
  // admins / before permissions load / when the lab configures no MAC (see useNavActions).
  //   Delete → Delete · Import + inline Event Date edit/Save → Write.
  // Export / Reports / Refresh are read-level (visible whenever the form itself is).
  const perm = useNavActions('sampaloc');
  // Date Range default = 3M — follows CLAUDE.md §14b.6 (DEFAULT_DATE_RANGE.SampleTracking,
  // "GoLive Settings → Default Settings → Date Filter") and the prototype, same as every
  // other Sample-Tracking list. The full 347-row set (GoLive's unfiltered load) stays one
  // click away via the Date Range → All option; the SP itself is unfiltered.
  const [dateRange,    setDateRange]    = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SampleTracking);
  const [search,       setSearch]       = useState('');
  // AppDataGrid owns sort / per-column filter / pagination; this page keeps only
  // the selection set (driven by AppDataGrid's checkbox column) for the Reports ▾ dropdown.
  const [sel,          setSel]          = useState<Set<string | number>>(new Set());
  // Reports ▾ dropdown — GoLive Custom Report Builder / Form Report Permissions
  // (templates assigned to Samplecheckin_ListView_SamplingAllocation). Dynamic, not
  // hardcoded: assigning a template to the form makes it appear here.
  const [reportsOpen,  setReportsOpen]  = useState(false);
  const [reportItems,  setReportItems]  =
    useState<{ id: number; label: string; templateName: string; allowMultipleJobId: boolean }[]>([]);
  // Drill-down popup — GoLive "click a grid row → SampleLogIn popup" (that job's samples).
  const [sampleJob,     setSampleJob]     = useState<string | null>(null);
  const [samples,       setSamples]       = useState<SamplingAllocationSample[]>([]);
  const [samplesLoading,setSamplesLoading]= useState(false);
  const [sampleSearch,  setSampleSearch]  = useState('');                        // popup Search box (GoLive parity)
  const [sampleSel,     setSampleSel]     = useState<Set<string | number>>(new Set()); // popup checkbox column
  const sampleReqRef = useRef(0);          // monotonic id of the latest samples fetch (race guard)
  // Popup "Assign To" editing — the ONE editable column (GoLive shows the dropdown but
  // never commits, §18.7; here it COMMITS). Pending edits map SampleLogIn.Oid → the
  // chosen employee DisplayName; the employee roster + its name→Oid map back the dropdown.
  const [assignEdits,   setAssignEdits]   = useState<Map<string, string>>(new Map());
  const [assignOptions, setAssignOptions] = useState<string[]>([]);          // employee DisplayNames (dropdown)
  const assignOidByName = useRef<Map<string, string>>(new Map());            // DisplayName → Employee Oid (for save)
  // Export (month/year schedule Excel) + Import (bulk upload) dialogs. Export defaults
  // to NEXT month (GoLive ExportExcel_Execute default).
  const [exportOpen,   setExportOpen]   = useState(false);
  // Default Month = NEXT month (GoLive ExportExcel_Execute). Computed from the month
  // INDEX only — NOT Date.setMonth(+1), which overflows on the 31st (Aug 31 → "Sep 31" →
  // Oct 1) and jumped a month too far.  (currentMonth0 + 1) % 12, back to 1-based.
  const [exportMonth,  setExportMonth]  = useState(() => (new Date().getMonth() + 1) % 12 + 1);
  // GoLive "Export Excel" uses a RELATIVE year (Previous / Current / Next Year). Default
  // matches GoLive: Current Year, unless next month rolls into January (then Next Year).
  const [exportYearMode, setExportYearMode] = useState<'prev' | 'cur' | 'next'>(
    () => new Date().getMonth() === 11 ? 'next' : 'cur');
  const [importOpen,   setImportOpen]   = useState(false);
  const [importFile,   setImportFile]   = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{ updated: number; skipped: number; errors: string[] } | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);   // hidden <input type=file>, driven by the Browse… button
  // "Save changes" batch edit — pending Event Date edits (jobId → new wire date).
  // GoLive main-grid Save parity, done correctly with a commit.
  const [edits,        setEdits]        = useState<Map<string, string>>(new Map());
  const notify    = useNotify();
  const { confirm: confirmDlg } = useConfirm();
  const gateReason = useReasonGate();
  const abortRef  = useRef<AbortController>();

  const loadData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(undefined);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: SamplingAllocationRow[] }>(
        '/api/v1/sampling/allocation', { signal: ctrl.signal });
      if (!ctrl.signal.aborted) {
        if (res.data.success) setRows(res.data.data);
        else setError('Failed to load sampling allocation data.');
      }
    } catch (e: unknown) {
      if (!ctrl.signal.aborted) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        if (msg !== 'canceled' && msg !== 'CanceledError') setError(msg);
      }
    } finally {
      if (!ctrl.signal.aborted) {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  // Reports ▾ menu — dynamic (GoLive parity): templates assigned to
  // Samplecheckin_ListView_SamplingAllocation via Custom Report Builder.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/v1/sampling/allocation/reports');
        setReportItems((res.data as { data?: typeof reportItems }).data ?? []);
      } catch { /* leave empty → the Reports ▾ button stays hidden */ }
    })();
  }, []);
  useEffect(() => { setSel(new Set()); }, [rows, search, statusFilter, dateRange]);
  // Fresh data (initial load / refresh / post-save) discards any pending edits.
  useEffect(() => { setEdits(new Map()); }, [rows]);

  // Date range filter
  const dateFiltered = useMemo(() => {
    const cutoff = cutoffDate(dateRange);
    if (!cutoff) return rows;
    return rows.filter(r => {
      const d = r.jobIDCreatedDate ? new Date(r.jobIDCreatedDate) : null;
      return d && !isNaN(d.getTime()) && d >= cutoff;
    });
  }, [rows, dateRange]);

  // Status + global search (per-column filter + sort live in AppDataGrid)
  const filtered = useMemo(() => {
    let result = dateFiltered;
    if (statusFilter !== 'all') result = result.filter(r => r.status === statusFilter);
    const s = search.toLowerCase().trim();
    if (s) result = result.filter(row =>
      CSV_COLS.some(c => { const v = row[c.key]; return v && String(v).toLowerCase().includes(s); })
    );
    return result;
  }, [dateFiltered, search, statusFilter]);

  // Rows shown in the grid = filtered rows with any pending Event Date edits overlaid.
  const displayRows = useMemo(
    () => edits.size === 0
      ? filtered
      : filtered.map(r => edits.has(r.jobId) ? { ...r, eventDate: edits.get(r.jobId)! } : r),
    [filtered, edits]);

  // Event Date cell committed → stage the edit (drop it if it matches the saved value).
  const onEventDateCommit = useCallback(
    (row: SamplingAllocationRow, col: AppColumn<SamplingAllocationRow>, val: string) => {
      if (col.field !== 'eventDate') return;
      setEdits(prev => {
        const next = new Map(prev);
        const orig = rows.find(r => r.jobId === row.jobId)?.eventDate ?? '';
        if (val === orig) next.delete(row.jobId); else next.set(row.jobId, val);
        return next;
      });
    }, [rows]);

  const cancelEdits = useCallback(() => { setEdits(new Map()); }, []);

  // Save changes → reason gate (standing rule 11, config-driven) → confirm → PUT
  // (audited server-side) → success only after commit → reload.
  const saveEdits = useCallback(async () => {
    if (edits.size === 0) return;
    const reason = await gateReason('Sampling Allocation', 'edit');
    if (reason === null) return;                    // user cancelled the reason prompt
    const ok = await confirmDlg({ message: 'Do you want to Save?', tone: 'default' });
    if (!ok) return;
    const payload = {
      edits: Array.from(edits.entries()).map(([jobId, eventDate]) => ({ jobId, eventDate })),
      reason: reason || null,                        // '' when ReasonRequired is off
    };
    try {
      await runLoading(
        api.put('/api/v1/sampling/allocation/schedule', payload),
        { blocking: true, label: 'Saving changes…' });
      // Centered success popup (BO UI Standard §13 milestone success) — user asked
      // for a centre dialog on Event Date save rather than the transient top banner.
      await showDialog({ type: 'success', title: 'Saved', message: 'Saved successfully.' });
      setEdits(new Map());
      await loadData();
    } catch {
      notify.error('Failed to save changes.');
    }
  }, [edits, gateReason, confirmDlg, notify, loadData]);

  // Delete (GoLive framework DeleteAction) → reason gate → danger confirm → soft-delete
  // the ticked job(s), audited server-side → success → reload.
  const deleteJobs = useCallback(async () => {
    const ids = Array.from(sel).map(String);
    if (ids.length === 0) return;
    const reason = await gateReason('Sampling Allocation', 'delete');
    if (reason === null) return;
    const ok = await confirmDlg({
      title: 'Delete',
      message: `Delete ${ids.length} selected job${ids.length === 1 ? '' : 's'}? ` +
               `This removes ${ids.length === 1 ? 'it' : 'them'} from the Sampling Allocation list.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await runLoading(
        api.post('/api/v1/sampling/allocation/delete', { jobIds: ids, reason: reason || null }),
        { blocking: true, label: 'Deleting…' });
      const n = (res.data?.data?.deleted as number) ?? 0;
      await showDialog({ type: 'success', title: 'Deleted', message: `${n} job${n === 1 ? '' : 's'} deleted.` });
      setSel(new Set());
      await loadData();
    } catch {
      notify.error('Failed to delete the selected job(s).');
    }
  }, [sel, gateReason, confirmDlg, notify, loadData]);

  // Export (GoLive ExportRecurrence) → download the chosen month/year schedule .xlsx.
  const downloadSchedule = useCallback(async () => {
    const cy = new Date().getFullYear();
    const year = exportYearMode === 'prev' ? cy - 1 : exportYearMode === 'next' ? cy + 1 : cy;
    try {
      const res = await runLoading(
        api.get('/api/v1/sampling/allocation/export-schedule',
          { params: { month: exportMonth, year }, responseType: 'blob' }),
        { blocking: true, label: 'Exporting schedule…' });
      const blob = res.data as Blob;
      const cd = (res.headers?.['content-disposition'] as string | undefined) ?? '';
      const fileName = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd)?.[1]
        ?? `${MONTHS[exportMonth - 1]}-${year}.xlsx`;
      triggerDownload(blob, decodeURIComponent(fileName));
      setExportOpen(false);
      // Centred success popup (not the transient top banner), per the two-tier standard.
      await showDialog({ type: 'success', title: 'Exported', message: 'Exported to Excel successfully.' });
    } catch {
      notify.error('Failed to export the schedule.');
    }
  }, [exportMonth, exportYearMode, notify]);

  // Import (GoLive ImportRecurrence) → upload the filled schedule .xlsx → bulk reschedule.
  const runImport = useCallback(async () => {
    if (!importFile) return;
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await runLoading(
        api.post('/api/v1/sampling/allocation/import-schedule', fd),
        { blocking: true, label: 'Importing…' });
      const data = res.data?.data as { updated: number; skipped: number; errors: string[] };
      setImportResult(data);
      if (data.updated > 0) await loadData();
      // Clean run → close + centred success (GoLive shows an "Imported" message on OK).
      // If any rows errored, keep the dialog open so the user can see which.
      if (!data.errors || data.errors.length === 0) {
        setImportOpen(false);
        await showDialog({
          type: 'success', title: 'Imported',
          message: `${data.updated} job${data.updated === 1 ? '' : 's'} updated` +
                   (data.skipped ? `, ${data.skipped} skipped.` : '.'),
        });
      }
    } catch {
      notify.error('Failed to import the file.');
    }
  }, [importFile, loadData, notify]);

  // Reports ▾ → render the chosen GoLive report for the ticked job(s) and open the
  // PDF in a new tab (GoLive ReportsAction_Execute — the selection feeds the report).
  const runReport = useCallback(async (it: { templateName: string; label: string }) => {
    setReportsOpen(false);
    const jobIds = Array.from(sel).map(String);
    if (jobIds.length === 0) {
      // Tier-1 centred modal (§18.6): action-guard notices use warning, not the top banner.
      notify.warning('Please select at least one record to preview the report.');
      return;
    }
    try {
      const res = await runLoading(
        api.get('/api/v1/sampling/allocation/report-pdf', {
          params: { templateName: it.templateName, jobIds: jobIds.join(',') },
          responseType: 'blob',
        }),
        { blocking: true, label: `Generating ${it.label}…` });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        // Tier-1 centred modal (§18.6) — user wants this as a centre popup, not a top banner.
        notify.warning(`No data to render for ${it.label}.`);
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      notify.error(`Failed to generate ${it.label}.`);
    }
  }, [sel, notify]);

  // Row click → open the samples popup for that job (GoLive SampleSourcePopup →
  // SampleLogIn list filtered by JobID). The checkbox column stops propagation, so
  // ticking a row for the Reports ▾ dropdown never opens the popup.
  const openSamples = useCallback(async (row: SamplingAllocationRow) => {
    // Race guard: only the LATEST open may apply its response. Without this, a
    // slow fetch from a previously-opened/closed job can land after a newer open
    // and overwrite the current popup with stale or empty data (the "sometimes
    // shows, sometimes not" bug — the backend always returns the rows).
    const reqId = ++sampleReqRef.current;
    setSampleJob(row.jobId);
    setSamples([]);
    setSampleSearch('');            // fresh Search box per job
    setSampleSel(new Set());        // fresh selection per job
    setAssignEdits(new Map());      // no pending Assign To edits for a freshly opened job
    setSamplesLoading(true);

    // One fetch attempt → the sample rows (throws on network/HTTP error).
    const fetchRows = async (): Promise<SamplingAllocationSample[]> => {
      const res = await api.get<{ success: boolean; data: SamplingAllocationSample[] }>(
        '/api/v1/sampling/allocation/samples', { params: { jobId: row.jobId } });
      return res.data.success ? res.data.data : [];
    };

    try {
      let rows = await fetchRows();
      // Every allocation job HAS samples (verified: 0 of 345 empty), so an empty or
      // failed first response is a transient client hiccup — retry ONCE before showing
      // an empty popup. This, with the race guard, closes the "sometimes empty" report.
      if (sampleReqRef.current === reqId && rows.length === 0) {
        try { rows = await fetchRows(); } catch { /* keep the empty result */ }
      }
      if (sampleReqRef.current !== reqId) return;   // superseded by a newer open — ignore
      setSamples(rows);
    } catch {
      try {
        const rows = await fetchRows();             // retry once on error
        if (sampleReqRef.current !== reqId) return;
        setSamples(rows);
      } catch {
        if (sampleReqRef.current === reqId) notify.error('Failed to load samples for this job.');
      }
    } finally {
      if (sampleReqRef.current === reqId) setSamplesLoading(false);
    }
  }, [notify]);

  // Close the popup and invalidate any in-flight samples fetch (so a late
  // response can't repopulate a closed/next popup).
  const closeSamples = useCallback(() => {
    sampleReqRef.current++;
    setSampleJob(null);
    setSamples([]);
    setAssignEdits(new Map());     // drop any unsaved Assign To edits on close
    setSamplesLoading(false);
  }, []);

  // Re-fetch the current job's samples (after a successful Assign To save) so the grid
  // shows the committed AssignTo + the fresh DateAssigned / AssignedBy stamps.
  const refetchSamples = useCallback(async (jobId: string) => {
    const reqId = ++sampleReqRef.current;
    setSamplesLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: SamplingAllocationSample[] }>(
        '/api/v1/sampling/allocation/samples', { params: { jobId } });
      if (sampleReqRef.current !== reqId) return;
      setSamples(res.data.success ? res.data.data : []);
    } catch {
      if (sampleReqRef.current === reqId) notify.error('Failed to reload samples.');
    } finally {
      if (sampleReqRef.current === reqId) setSamplesLoading(false);
    }
  }, [notify]);

  // Employee roster for the Assign To dropdown — fetched ONCE, the first time an
  // editor opens the popup (skipped for read-only users). DisplayName is the E-code
  // shown on the legacy lookups; the name→Oid map resolves the save payload.
  useEffect(() => {
    if (sampleJob === null || !perm.write || assignOptions.length) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: { oid: string; userName: string; fullName: string; displayName: string }[] }>(
          '/api/v1/lookups/employees');
        if (!alive) return;
        const map = new Map<string, string>();
        const names: string[] = [];
        for (const e of res.data?.data ?? []) {
          const label = e.displayName || e.userName || e.fullName;
          if (!label || map.has(label)) continue;
          map.set(label, e.oid);
          names.push(label);
        }
        assignOidByName.current = map;
        setAssignOptions(names);
      } catch { /* dropdown stays empty; a save then reports the failure via notify */ }
    })();
    return () => { alive = false; };
  }, [sampleJob, perm.write, assignOptions.length]);

  // Stage one Assign To edit (SampleLogIn.Oid → chosen DisplayName). Clearing back to the
  // original value drops the pending edit so an unchanged row is never saved.
  const onAssignCommit = useCallback(
    (row: SamplingAllocationSample, col: AppColumn<SamplingAllocationSample>, val: string) => {
      if (col.field !== 'assignTo') return;
      setAssignEdits(prev => {
        const next = new Map(prev);
        const orig = samples.find(s => s.sampleLogInId === row.sampleLogInId)?.assignTo ?? '';
        if (val === orig) next.delete(row.sampleLogInId); else next.set(row.sampleLogInId, val);
        return next;
      });
    }, [samples]);

  const cancelAssignments = useCallback(() => setAssignEdits(new Map()), []);

  // Popup "Save changes" → reason gate (rule 11) → confirm → PUT (audited server-side,
  // Write-gated) → success only after commit → reload the popup rows.
  const saveAssignments = useCallback(async () => {
    if (assignEdits.size === 0) return;
    const reason = await gateReason('Sampling Allocation', 'edit');
    if (reason === null) return;
    const ok = await confirmDlg({ message: 'Do you want to Save?', tone: 'default' });
    if (!ok) return;
    const EMPTY = '00000000-0000-0000-0000-000000000000';   // clears the assignment
    const assignments = Array.from(assignEdits.entries()).map(([sampleLogInId, name]) => ({
      sampleLogInId,
      assignTo: assignOidByName.current.get(name) ?? EMPTY,
    }));
    try {
      await runLoading(
        api.put('/api/v1/sampling/allocation/assign', { assignments, reason: reason || null }),
        { blocking: true, label: 'Saving changes…' });
      await showDialog({ type: 'success', title: 'Saved', message: 'Saved successfully.' });
      setAssignEdits(new Map());
      if (sampleJob) await refetchSamples(sampleJob);
    } catch {
      notify.error('Failed to save changes.');
    }
  }, [assignEdits, gateReason, confirmDlg, notify, sampleJob, refetchSamples]);

  // Popup grid columns — GoLive SampleLogIn_ListView_SamplingAllocation.
  // Row height is kept to a single line (feedback 2026-09-01: "each row cannot be
  // more than two lines tall — use the horizontal scroll bar"): NO column wraps, so
  // the long free-text Sampling Equipment stays on one line and the grid scrolls
  // HORIZONTALLY (AppDataGrid without `fixedLayout` → the table sizes to content and
  // `.adg-scroll` shows a horizontal bar) instead of blowing the row height up.
  const sampleCols: AppColumn<SamplingAllocationSample>[] = [
    { field: 'sampleMatrix',      header: 'Sample Matrix',      width: 120 },
    { field: 'siteName',          header: 'Site Name',          width: 105 },
    { field: 'sampleId',          header: 'Sample ID',          width: 125 },
    { field: 'sampleName',        header: 'Sample Name',        width: 120 },
    { field: 'samplingDate',      header: 'Sampling Date',      width: 125,
      render: r => fmtDate(r.samplingDate), text: r => fmtDate(r.samplingDate) },
    // The ONE editable column — an employee dropdown, click-to-edit, committed on
    // "Save changes" (GoLive shows this but never commits, §18.7). Editor only for
    // MAC-Write users; read-only users see plain text.
    { field: 'assignTo',          header: 'Assign To',          width: 130,
      ...(perm.write
        ? { edit: { type: 'combo' as const, options: assignOptions, trigger: 'click' as const } }
        : {}) },
    { field: 'dateAssigned',      header: 'Date Assigned',      width: 125,
      render: r => fmtDate(r.dateAssigned), text: r => fmtDate(r.dateAssigned) },
    { field: 'assignedBy',        header: 'Assigned By',        width: 130 },
    // Sampling Equipment MOVED to the far right (feedback 2026-09-01: "the Sampling
    // Equipment can be moved to right side") so the compact columns show first. It's
    // the wide free-text column: ONE line, no ellipsis (`noClip`) so it grows to its
    // full width and the FULL value is reached via the horizontal scroll bar — keeping
    // every row one line tall (feedback 2026-09-01: rows max two lines, use h-scroll).
    { field: 'samplingEquipment', header: 'Sampling Equipment', width: 300, noClip: true },
  ];

  // Popup Search box — GoLive's popup "Search" (global, all columns, case-insensitive).
  const filteredSamples = useMemo(() => {
    const q = sampleSearch.trim().toLowerCase();
    if (!q) return samples;
    return samples.filter(s =>
      [s.sampleMatrix, s.siteName, s.sampleId, s.sampleName, s.samplingEquipment,
       fmtDate(s.samplingDate), s.assignTo, fmtDate(s.dateAssigned), s.assignedBy]
        .some(v => (v ?? '').toLowerCase().includes(q)));
  }, [samples, sampleSearch]);

  // Overlay pending Assign To edits onto the displayed rows (same pattern the main grid
  // uses for Event Date, line ~223) so a picked employee shows immediately, without
  // mutating `samples` — Cancel just drops the edits and the display reverts.
  const displaySamples = useMemo(
    () => (assignEdits.size === 0
      ? filteredSamples
      : filteredSamples.map(s =>
          assignEdits.has(s.sampleLogInId) ? { ...s, assignTo: assignEdits.get(s.sampleLogInId)! } : s)),
    [filteredSamples, assignEdits]);

  // Shared-grid columns.  Date columns format via fmtDate; Status keeps its
  // original three-colour pill (warn / ok / info) via a custom renderer.
  const cols: AppColumn<SamplingAllocationRow>[] = [
    { field: 'jobId',            header: 'Job ID',       width: 90 },
    { field: 'client',           header: 'Client',       width: 150 },
    { field: 'projectName',      header: 'Project Name', width: 160 },
    { field: 'projectCode',      header: 'Project Code', width: 130 },
    { field: 'jobIDCreatedDate', header: 'Job Created',  width: 90,
      render: r => fmtDate(r.jobIDCreatedDate), text: r => fmtDate(r.jobIDCreatedDate) },
    // Editable Event Date (DateExpect) — GoLive's one inline-editable column. Click to
    // edit (the editor stops propagation, so it never opens the samples popup); commits
    // stage into `edits` and persist via Save changes.
    // Inline edit is a WRITE action. Keep the cell an EDIT cell EITHER WAY so a click is
    // swallowed by the InlineCellEditor and never falls through to the row-click → samples
    // popup (GoLive parity: Event Date is a batch-edit cell — greyed for a no-Write user —
    // and clicking it never opens the drill-down). `disabled` (no MAC Write) → read-only
    // text, click inert, no edits staged; MAC Write → click-to-edit as before.
    { field: 'eventDate',        header: 'Event Date',   width: 120,
      edit: { type: 'date', trigger: 'click', disabled: () => !perm.write },
      render: r => fmtDate(r.eventDate), text: r => fmtDate(r.eventDate) },
    { field: 'updatedDate',      header: 'Updated Date', width: 90,
      render: r => fmtDate(r.updatedDate), text: r => fmtDate(r.updatedDate) },
    { field: 'status', header: 'Status', width: 130,
      text: r => r.status || '',
      render: r => {
        const s = r.status || '';
        // Colour by status FAMILY (prefix) so the whole pending family reads amber
        // and the whole submitted family reads green — matches the KPI bucketing.
        const cls = s.startsWith('Pending') ? 'warn' : s.startsWith('Submitted') ? 'ok' : 'info';
        return <span className={`bl-pill ${cls}`}>{s || '—'}</span>;
      },
    },
  ];

  // KPI stats (based on dateFiltered — matches whatever date range is selected).
  // Bucket by status FAMILY (prefix), NOT an exact single label, so Submitted +
  // Pending account for EVERY job and reconcile with Total. The live set carries
  // four statuses ('Pending Sampling', 'Pending Transfer', 'Submitted',
  // 'Submitted, Pending Field Data Entry'); the old exact match counted only the
  // first of each family, leaving 'Pending Transfer' + 'Submitted, Pending Field
  // Data Entry' out so the cards under-counted (e.g. 146+259=405 vs 408 Total).
  // Mirrors the AI_SamplingAllocation_Counts_SP prefix bucketing.
  const total      = dateFiltered.length;
  const submitted  = dateFiltered.filter(r => (r.status ?? '').startsWith('Submitted')).length;
  const pending    = dateFiltered.filter(r => (r.status ?? '').startsWith('Pending')).length;
  const subPct     = total > 0 ? Math.round(submitted / total * 100) : 0;
  const pendPct    = total > 0 ? Math.round(pending   / total * 100) : 0;
  const eventDate  = mostRecentEventDate(dateFiltered);

  // Status filter chips — DATA-DRIVEN so every status present in the data gets a
  // chip (not just Submitted / Pending Sampling), the chips mirror the Status
  // column filter's list, and the per-chip counts always sum to All. Status is
  // ALPACA's Samplecheckin.[Index] -> StatusDefinition.Status. Ordered by count
  // desc (Submitted, Pending Sampling, then the smaller queues).
  const statusTabs: { id: StatusFilter; label: string }[] = (() => {
    const counts = new Map<string, number>();
    for (const r of dateFiltered) {
      const s = r.status || '';
      if (s) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    const ordered = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    return [
      { id: 'all', label: `All (${dateFiltered.length})` },
      ...ordered.map(([s, n]) => ({ id: s, label: `${s} (${n})` })),
    ];
  })();

  // Phase 2.3 — Sampling Allocation keys (CLAUDE.md standing rule 14, §18.6,
  // ADR-0013): this form builds its whole action bar in the <AppToolbar> `left`
  // slot, so the toolbar's built-in shortcuts don't apply — register them here to
  // mirror each button (same handler + disabled/gate).  Ctrl+S Save changes ·
  // Alt+D Delete · Alt+R Refresh · Alt+I Import · Alt+X Export · Esc Cancel
  // changes.  Delete/Import are MAC-gated (registered only when their button
  // shows).  Esc is allowInInput:false so it never discards Event Date edits while
  // a cell editor is focused; AppDialog is aria-modal so open popups own the keys.
  const hotkeys: HotkeyDef[] = [
    { combo: 'mod+s', label: 'Save changes', group: 'Sampling Allocation', order: 1,
      run: () => void saveEdits(), disabled: edits.size === 0 },
  ];
  if (reportItems.length > 0)
    hotkeys.push({ combo: 'alt+o', label: 'Reports', group: 'Sampling Allocation', order: 2,
      run: () => setReportsOpen(o => !o), disabled: sel.size === 0 });
  if (perm.delete)
    hotkeys.push({ combo: 'alt+d', label: 'Delete', group: 'Sampling Allocation', order: 3,
      run: () => void deleteJobs(), disabled: sel.size === 0 });
  hotkeys.push({ combo: 'alt+r', label: 'Refresh', group: 'Sampling Allocation', order: 5,
    run: () => void loadData(), disabled: loading });
  if (perm.write)
    hotkeys.push({ combo: 'alt+i', label: 'Import', group: 'Sampling Allocation', order: 6,
      run: () => { setImportResult(null); setImportFile(null); setImportOpen(true); } });
  hotkeys.push({ combo: 'alt+x', label: 'Export', group: 'Sampling Allocation', order: 7,
    run: () => setExportOpen(true) });
  hotkeys.push({ combo: 'esc', label: 'Cancel changes', group: 'Sampling Allocation', order: 9,
    allowInInput: false, run: () => cancelEdits(), disabled: edits.size === 0 });
  useHotkeys(hotkeys);

  return (
    <div className="bl-page">

      {/* ── Fixed top ── */}
      <div className="bl-top">

        {/* Page header */}
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sample Scheduling</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Sampling Allocation</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta + the TOP load-time chip. */}
          <div className="dp-title-row">
            <FormTitle pageId="sampaloc" auditFormName="Sampling Allocation" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            Read-only · {loading ? '…' : rows.length} items
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}

        {/* KPI row — matches v5 layout */}
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-lbl">Total Items</div>
            <div className="kpi-val kpi-blue">{loading ? '…' : total}</div>
            <div className="kpi-chg">Items in range</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Submitted</div>
            <div className="kpi-val kpi-teal">{loading ? '…' : submitted}</div>
            <div className="kpi-chg" style={{ color: 'var(--ai-green)' }}>↑ {subPct}%</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Pending</div>
            <div className="kpi-val kpi-red">{loading ? '…' : pending}</div>
            <div className="kpi-chg" style={{ color: 'var(--ai-red)' }}>↓ {pendPct}%</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Event Date</div>
            <div className="kpi-val" style={{ fontSize: 16 }}>{loading ? '…' : eventDate}</div>
            <div className="kpi-chg">Most recent run</div>
          </div>
        </div>

      </div>

      {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. ── */}
      <div className="bl-grid-area">
        {/* Toolbar — button order per the form owner: Date Range · Refresh, then the
            action group Reports · Delete · Export · Import. Rendered in the left slot so
            the four actions stay contiguous in that exact order (the shared AppToolbar's
            fixed slots would put Refresh between Delete and Import). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />

                {/* Reports ▾ (GoLive Reports dropdown). DB-driven; needs a row selection. */}
                {reportItems.length > 0 && (
                  <span className="app-toolbar__export">
                    <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}
                            disabled={sel.size === 0}
                            title={(sel.size === 0 ? 'Select at least one row' : 'Reports') + ` (${formatCombo('alt+o')})`}>
                      <span className="tb-i tb-i-blue"><i className="ti ti-file-text" /></span> Reports ▾
                    </button>
                    {reportsOpen && (
                      <>
                        <div className="app-toolbar__export-backdrop" onClick={() => setReportsOpen(false)} />
                        <div className="app-toolbar__export-menu" role="menu">
                          {reportItems.map(it => (
                            <button key={it.id} className="app-toolbar__export-item" role="menuitem"
                                    onClick={() => runReport(it)}>
                              <i className="ti ti-file-text" /> {it.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </span>
                )}

                {/* Delete (GoLive framework DeleteAction) — MAC Delete-gated; needs a selection. */}
                {perm.delete && (
                  <button className="ca-btn" onClick={deleteJobs} disabled={sel.size === 0} title={`Delete (${formatCombo('alt+d')})`}>
                    <span className="tb-i tb-i-red">🗑</span> Delete
                  </button>
                )}

                {/* Export (GoLive ExportRecurrence) — monthly schedule Excel. */}
                <button className="ca-btn" onClick={() => setExportOpen(true)}
                        title={`Export the monthly sampling schedule to Excel (${formatCombo('alt+x')})`}>
                  <span className="tb-i tb-i-green"><i className="ti ti-calendar-down" /></span> Export
                </button>

                {/* Import (GoLive ImportRecurrence) — MAC Write-gated; upload the filled schedule. */}
                {perm.write && (
                  <button className="ca-btn" title={`Import (${formatCombo('alt+i')})`}
                          onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }}>
                    <span className="tb-i tb-i-green">📥</span> Import
                  </button>
                )}

                {/* Refresh — placed next to Import per the form owner. */}
                <button className="ca-btn" onClick={loadData} disabled={loading} title={`Refresh (${formatCombo('alt+r')})`}>
                  {loading ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>} Refresh
                </button>
              </>
            }
          />
        </div>

        {/* Search row — shared .list-search-row on the grey page, never inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              placeholder="Search job ID, client, project…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== dateFiltered.length && (
            <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
          )}
        </div>

        {/* Unsaved-edits bar — appears when Event Date(s) have been edited. GoLive's
            main-grid Save/Cancel changes, done correctly (persists + audits on Save). */}
        {edits.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px',
                        padding: '6px 10px', background: '#fffbe7',
                        border: '1px solid #f0e2a8', borderRadius: 6 }}>
            <i className="ti ti-pencil" />
            <span style={{ fontSize: 13 }}>
              {edits.size} unsaved Event Date change{edits.size === 1 ? '' : 's'}
            </span>
            <span style={{ flex: 1 }} />
            <button className="ca-btn" onClick={saveEdits} title={`Save changes (${formatCombo('mod+s')})`}>
              <span className="tb-i tb-i-green"><i className="ti ti-device-floppy" /></span> Save changes
            </button>
            <button className="ca-btn" onClick={cancelEdits} title={`Cancel changes (${formatCombo('esc')})`}>
              <span className="tb-i"><i className="ti ti-x" /></span> Cancel changes
            </button>
          </div>
        )}

        {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
        <div className="bl-card bl-card-flex">

          {/* Status filter row — matches v5 filter-row */}
          <div className="sa-filter-row">
            <span className="sa-filter-lbl">Status:</span>
            {statusTabs.map(t => (
              <button
                key={t.id}
                className={`sa-filter-btn${statusFilter === t.id ? ' active' : ''}`}
                onClick={() => setStatusFilter(t.id)}
              >{t.label}</button>
            ))}
          </div>

          {/* Shared data grid — styling, per-column filters, sort, footer /
             pagination, load-time and the selection checkboxes are all
             standardised in <AppDataGrid>. */}
          <AppDataGrid<SamplingAllocationRow>
            rows={displayRows}
            columns={cols}
            getRowId={r => r.jobId}
            loading={loading}
            loadMs={loadMs}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            onRowClick={openSamples}
            onCellCommit={onEventDateCommit}
            onCellChange={onEventDateCommit}
            emptyText="No items in this queue"
          />

        </div>
      </div>

      {/* Drill-down popup — a job's samples (GoLive click-row → SampleLogIn popup).
          Vertically CENTRED in the viewport (user request 2026-09-03) — `dlg-center`
          centres it while keeping the grid's internal scroll (header/pager pinned). */}
      <AppDialog
        open={sampleJob !== null}
        title={`Sampling Allocation — ${sampleJob ?? ''}`}
        icon="ti-test-pipe"
        width={1240}
        className="dlg-center"
        maximizable
        onClose={closeSamples}
        footer={perm.write ? (
          // "Save changes" / "Cancel changes" (GoLive popup parity — but these COMMIT).
          // Enabled only when there is a pending Assign To edit.
          <>
            <button className="ca-btn ca-btn-solid" disabled={assignEdits.size === 0} onClick={saveAssignments}
                    title="Save the Assign To changes">
              <span className="tb-i"><i className="ti ti-device-floppy" /></span> Save changes
            </button>
            <button className="ca-btn" disabled={assignEdits.size === 0} onClick={cancelAssignments}
                    title="Discard the Assign To changes">
              <span className="tb-i"><i className="ti ti-arrow-back-up" /></span> Cancel changes
            </button>
          </>
        ) : undefined}
      >
        {/* Search box — GoLive's popup Search (right-aligned above the grid). */}
        <div className="list-search-row" style={{ justifyContent: 'flex-end' }}>
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              placeholder="Search…"
              value={sampleSearch}
              onChange={e => setSampleSearch(e.target.value)}
            />
            {sampleSearch && (
              <span className="list-search-clear" onClick={() => setSampleSearch('')}>×</span>
            )}
          </div>
        </div>
        <AppDataGrid<SamplingAllocationSample>
          rows={displaySamples}
          columns={sampleCols}
          getRowId={r => r.sampleId ?? ''}
          loading={samplesLoading}
          defaultPageSize={20}
          selectable
          selectedIds={sampleSel}
          onSelectionChange={setSampleSel}
          onCellCommit={onAssignCommit}
          emptyText="No samples for this job"
        />
      </AppDialog>

      {/* Export dialog — GoLive "Export Excel" (ExportRecurrence): Year + Month → OK/Cancel. */}
      <AppDialog
        open={exportOpen}
        title="Export Excel"
        icon="ti-file-spreadsheet"
        width={420}
        maximizable
        onClose={() => setExportOpen(false)}
        footer={
          <>
            <button className="ca-btn" onClick={downloadSchedule}>
              <span className="tb-i tb-i-green"><i className="ti ti-check" /></span> OK
            </button>
            <button className="ca-btn" onClick={() => setExportOpen(false)}>
              <span className="tb-i"><i className="ti ti-x" /></span> Cancel
            </button>
          </>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 12px', alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Year:</label>
          <select value={exportYearMode} onChange={e => setExportYearMode(e.target.value as 'prev' | 'cur' | 'next')}
                  style={{ padding: '6px 8px', border: '1px solid var(--ai-line)', borderRadius: 6, fontSize: 13 }}>
            <option value="prev">Previous Year</option>
            <option value="cur">Current Year</option>
            <option value="next">Next Year</option>
          </select>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Month:</label>
          <select value={exportMonth} onChange={e => setExportMonth(Number(e.target.value))}
                  style={{ padding: '6px 8px', border: '1px solid var(--ai-line)', borderRadius: 6, fontSize: 13 }}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </AppDialog>

      {/* Import dialog — GoLive "Items File Upload" (ImportRecurrence → ItemsFileUpload):
          Input File + Browse, OK / Cancel. */}
      <AppDialog
        open={importOpen}
        title="Items File Upload"
        icon="ti-upload"
        width={460}
        maximizable
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <button className="ca-btn" onClick={runImport} disabled={!importFile}>
              <span className="tb-i tb-i-green"><i className="ti ti-check" /></span> OK
            </button>
            <button className="ca-btn" onClick={() => setImportOpen(false)}>
              <span className="tb-i"><i className="ti ti-x" /></span> Cancel
            </button>
          </>
        }
      >
        {/* GoLive "Items File Upload": empty → text box + Browse…; a file chosen → the
            filename as a link with a download icon + a clear (✕) icon (like the XAF
            FileData control), so the user can download or remove/replace it. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>Input File:</label>
          {importFile ? (
            <>
              <a onClick={() => importFileRef.current?.click()} title="Change file"
                 style={{ flex: 1, fontSize: 13, color: 'var(--ai-green)', textDecoration: 'underline',
                          cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {importFile.name}
              </a>
              {/* 1st icon — Change file (re-open the picker, GoLive pencil). */}
              <button title="Change file" onClick={() => importFileRef.current?.click()}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                               width: 26, height: 26, border: '1px solid var(--ai-line)', borderRadius: 6,
                               background: '#fff', cursor: 'pointer' }}>
                <i className="ti ti-pencil" />
              </button>
              {/* 2nd icon — Clear the selected file. */}
              <button title="Clear"
                      onClick={() => { setImportFile(null); setImportResult(null); if (importFileRef.current) importFileRef.current.value = ''; }}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                               width: 26, height: 26, border: '1px solid var(--ai-line)', borderRadius: 6,
                               background: '#fff', cursor: 'pointer', color: 'var(--ai-red)' }}>
                <i className="ti ti-x" />
              </button>
            </>
          ) : (
            <>
              <input type="text" readOnly value="" onClick={() => importFileRef.current?.click()}
                     style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--ai-line)', borderRadius: 6,
                              fontSize: 13, background: '#fff', cursor: 'pointer' }} />
              <button className="ca-btn" onClick={() => importFileRef.current?.click()}>
                <span className="tb-i"><i className="ti ti-folder" /></span> Browse…
              </button>
            </>
          )}
          <input ref={importFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                 onChange={e => { setImportFile(e.target.files?.[0] ?? null); setImportResult(null); }} />
        </div>
        {importResult && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div style={{ color: 'var(--ai-green)', fontWeight: 600 }}>✓ Updated: {importResult.updated}</div>
            <div style={{ color: 'var(--ai-muted)' }}>Skipped: {importResult.skipped}</div>
            {importResult.errors.length > 0 && (
              <ul style={{ marginTop: 6, color: 'var(--ai-red)', maxHeight: 160, overflow: 'auto', paddingLeft: 18 }}>
                {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            )}
          </div>
        )}
      </AppDialog>
    </div>
  );
}
